/**
 * Selector de parqueadero para la cabecera del sidebar.
 *
 * Antes solo se podía elegir parqueadero al iniciar sesión: para cambiar
 * había que cerrar sesión y volver a entrar. Aquí se elige desde dentro, con
 * buscador para cuando la lista es larga.
 */
import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Popover, TextField, List, ListItemButton,
  CircularProgress, InputAdornment, alpha, Tooltip,
} from '@mui/material';
import { LocalParking, Search, ExpandMore, Check } from '@mui/icons-material';
import apiClient from '../../utils/axios';
import { useAuth } from '../../context/AuthContext';

const ORANGE = '#F59E0B';
const WHITE  = '#FFFFFF';

const resolveUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${process.env.REACT_APP_API_URL || ''}${url}`;
};

function Logo({ url, size }) {
  return url
    ? <Box component="img" src={resolveUrl(url)} alt=""
        sx={{ width: size, height: size, borderRadius: 1.5, objectFit: 'contain', bgcolor: 'white', p: 0.25, flexShrink: 0 }} />
    : <Box sx={{ width: size, height: size, borderRadius: 1.5, bgcolor: ORANGE, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <LocalParking sx={{ color: WHITE, fontSize: size * 0.55 }} />
      </Box>;
}

export default function SelectorParqueadero({ abierto }) {
  const { user, clienteInfo, selectCliente } = useAuth();
  const [anchor, setAnchor]   = useState(null);
  const [lista, setLista]     = useState([]);
  const [cargando, setCargando] = useState(false);
  const [filtro, setFiltro]   = useState('');
  const [cambiando, setCambiando] = useState(null);

  // Se consulta al abrir: la lista del login se pierde al recargar la página
  useEffect(() => {
    if (!anchor) return;
    setCargando(true);
    apiClient.get('/api/auth/mis-parqueaderos')
      .then(res => setLista(res.data?.data || []))
      .catch(() => setLista([]))
      .finally(() => setCargando(false));
  }, [anchor]);

  const nombreActual = clienteInfo?.nombre || process.env.REACT_APP_NOMBRE_CONJUNTO || 'PARKEY';
  const idActual = user?.cliente_id;

  const visibles = lista.filter(c =>
    c.nombre?.toLowerCase().includes(filtro.toLowerCase()) ||
    c.slug?.toLowerCase().includes(filtro.toLowerCase())
  );

  const abrir = (e) => { setFiltro(''); setAnchor(e.currentTarget); };
  const cerrar = () => { setAnchor(null); setFiltro(''); };

  const elegir = async (cliente) => {
    if (cliente.id === idActual) { cerrar(); return; }
    setCambiando(cliente.id);
    try {
      await selectCliente(cliente.id);
      cerrar();
    } finally {
      setCambiando(null);
    }
  };

  return (
    <>
      <Tooltip title={abierto ? '' : `${nombreActual} — cambiar parqueadero`} placement="right" arrow>
        <Box onClick={abrir}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0,
            cursor: 'pointer', borderRadius: 2, p: abierto ? 0.5 : 0,
            transition: 'background-color 0.15s',
            '&:hover': { bgcolor: alpha(WHITE, 0.08) },
          }}>
          <Logo url={clienteInfo?.logo_url} size={abierto ? 36 : 34} />
          {abierto && (
            <>
              {/* minWidth 0 es lo que permite que noWrap recorte: sin eso el
                  flex hijo crece con el texto y desborda el sidebar. */}
              <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                <Typography variant="caption" fontWeight={700} noWrap
                  sx={{ color: WHITE, display: 'block', lineHeight: 1.3,
                        overflow: 'hidden', textOverflow: 'ellipsis' }}
                  title={nombreActual}>
                  {nombreActual}
                </Typography>
              </Box>
              <ExpandMore sx={{ color: alpha(WHITE, 0.45), fontSize: 18, flexShrink: 0 }} />
            </>
          )}
        </Box>
      </Tooltip>

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={cerrar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { borderRadius: 3, width: 300, mt: 0.5, overflow: 'hidden' } }}
      >
        <Box sx={{ p: 1.5, borderBottom: '1px solid #EEE' }}>
          <TextField
            autoFocus fullWidth size="small" placeholder="Buscar parqueadero..."
            value={filtro} onChange={e => setFiltro(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: '#999' }} /></InputAdornment>,
              sx: { borderRadius: 2 },
            }}
          />
        </Box>

        {cargando ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={22} sx={{ color: ORANGE }} />
          </Box>
        ) : visibles.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: '#999' }}>
              {lista.length === 0 ? 'No hay parqueaderos disponibles' : 'Ninguno coincide con la búsqueda'}
            </Typography>
          </Box>
        ) : (
          <List dense sx={{ maxHeight: 320, overflowY: 'auto', py: 0.5 }}>
            {visibles.map(c => {
              const actual = c.id === idActual;
              return (
                <ListItemButton key={c.id} onClick={() => elegir(c)}
                  disabled={cambiando !== null}
                  sx={{ gap: 1.5, py: 1, px: 1.5, bgcolor: actual ? alpha(ORANGE, 0.08) : 'transparent' }}>
                  <Logo url={c.logo_url} size={28} />
                  <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                    <Typography variant="body2" fontWeight={actual ? 700 : 500} noWrap
                      sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }} title={c.nombre}>
                      {c.nombre}
                    </Typography>
                    {c.slug && (
                      <Typography variant="caption" noWrap
                        sx={{ color: '#999', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {c.slug}
                      </Typography>
                    )}
                  </Box>
                  {cambiando === c.id
                    ? <CircularProgress size={16} sx={{ color: ORANGE }} />
                    : actual && <Check sx={{ fontSize: 18, color: ORANGE }} />}
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Popover>
    </>
  );
}
